import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { PublicContent } from '@/types';
import { Newspaper, Globe, FileText, Trash2, Send, Save } from 'lucide-react';

const CONTENT_TYPES: PublicContent['type'][] = ['announcement', 'news', 'achievement', 'event'];

const TYPE_STYLES: Record<PublicContent['type'], string> = {
  announcement: 'bg-willard-taupe text-tiger-primary-black',
  news: 'bg-tiger-neutral-200 text-tiger-neutral-700',
  achievement: 'bg-amber-100 text-amber-800',
  event: 'bg-blue-100 text-blue-800',
};

export const NewsTab: React.FC = () => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState<PublicContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Create form
  const [title, setTitle] = useState('');
  const [type, setType] = useState<PublicContent['type']>('announcement');
  const [content, setContent] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'publicContent'));
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate() || new Date(),
        publishedAt: d.data().publishedAt?.toDate() || undefined,
      })) as PublicContent[];
      data.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setItems(data);
      setLoadError('');
    } catch (error) {
      console.error('Error fetching public content:', error);
      setLoadError('Could not load content. Check your connection and permissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (publish: boolean) => {
    if (!title.trim() || !content.trim()) {
      setSaveError('Title and content are required.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await addDoc(collection(db, 'publicContent'), {
        type,
        title: title.trim(),
        content: content.trim(),
        isPublished: publish,
        authorId: currentUser?.uid || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(publish ? { publishedAt: serverTimestamp() } : {}),
      });
      setTitle('');
      setContent('');
      setType('announcement');
      await fetchItems();
    } catch (error) {
      console.error('Error creating content:', error);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (item: PublicContent) => {
    const next = !item.isPublished;
    const prev = items;
    setItems(list => list.map(i => (i.id === item.id ? { ...i, isPublished: next } : i)));
    try {
      await updateDoc(doc(db, 'publicContent', item.id), {
        isPublished: next,
        updatedAt: serverTimestamp(),
        ...(next ? { publishedAt: serverTimestamp() } : {}),
      });
    } catch (error) {
      console.error('Error toggling publish state:', error);
      setItems(prev);
    }
  };

  const handleDelete = async (item: PublicContent) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'publicContent', item.id));
      setItems(list => list.filter(i => i.id !== item.id));
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Failed to delete. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Create form */}
      <div className="bg-white rounded-2xl shadow-tiger-lg p-6">
        <h2 className="text-xl font-display font-black text-tiger-primary-black mb-4">
          Post News &amp; Announcements
        </h2>
        {saveError && (
          <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {saveError}
          </div>
        )}
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="news-title" className="block text-sm font-bold text-tiger-neutral-700 mb-2">
                Title
              </label>
              <input
                type="text"
                id="news-title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full px-4 py-3 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition"
                placeholder="e.g., Tigers take second at districts"
              />
            </div>
            <div>
              <label htmlFor="news-type" className="block text-sm font-bold text-tiger-neutral-700 mb-2">
                Type
              </label>
              <select
                id="news-type"
                value={type}
                onChange={e => setType(e.target.value as PublicContent['type'])}
                className="w-full px-4 py-3 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition"
              >
                {CONTENT_TYPES.map(t => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="news-content" className="block text-sm font-bold text-tiger-neutral-700 mb-2">
              Content
            </label>
            <textarea
              id="news-content"
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition resize-none"
              placeholder="Write the announcement..."
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => handleCreate(false)}
              disabled={saving}
              className="px-6 py-3 border-2 border-tiger-neutral-300 text-tiger-neutral-700 font-bold rounded-lg hover:bg-tiger-neutral-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save draft
            </button>
            <button
              type="button"
              onClick={() => handleCreate(true)}
              disabled={saving}
              className="px-6 py-3 bg-tiger-primary-black text-white font-bold rounded-lg hover:bg-tiger-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Publish now
            </button>
          </div>
        </div>
      </div>

      {/* Existing items */}
      <div className="bg-white rounded-2xl shadow-tiger-lg overflow-hidden">
        <div className="p-6 border-b border-tiger-neutral-200">
          <h2 className="text-xl font-display font-black text-tiger-primary-black">
            Published &amp; Draft Content
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tiger-primary-black"></div>
          </div>
        ) : loadError ? (
          <p className="text-red-600 text-center py-12">{loadError}</p>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <Newspaper className="w-16 h-16 text-tiger-neutral-300 mx-auto mb-4" />
            <p className="text-tiger-neutral-600">Nothing posted yet</p>
          </div>
        ) : (
          <div className="divide-y divide-tiger-neutral-200">
            {items.map(item => (
              <div
                key={item.id}
                className="p-6 flex flex-wrap items-center justify-between gap-4 hover:bg-tiger-neutral-50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-tiger-primary-black">{item.title}</h3>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-bold ${TYPE_STYLES[item.type] || TYPE_STYLES.news}`}
                    >
                      {item.type}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        item.isPublished
                          ? 'bg-green-100 text-green-800'
                          : 'bg-tiger-neutral-200 text-tiger-neutral-600'
                      }`}
                    >
                      {item.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-sm text-tiger-neutral-500 mt-1">
                    Created {item.createdAt.toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTogglePublish(item)}
                    title={item.isPublished ? 'Unpublish (hide from Home page)' : 'Publish to Home page'}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
                      item.isPublished
                        ? 'bg-tiger-neutral-100 text-tiger-neutral-700 hover:bg-tiger-neutral-200'
                        : 'bg-tiger-primary-black text-white hover:bg-tiger-neutral-800'
                    }`}
                  >
                    {item.isPublished ? (
                      <>
                        <FileText className="w-3.5 h-3.5" />
                        Unpublish
                      </>
                    ) : (
                      <>
                        <Globe className="w-3.5 h-3.5" />
                        Publish
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    title="Delete"
                    className="p-2 text-tiger-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
